import { moment } from 'utils';
import propertyObjectTypes from 'constants/propertyObjectTypes';
import { filter, forEach, isEmpty, maxBy, minBy, unionBy } from 'lodash';

function extendAccrualHistory(accrualHistory = [], costHistory = []) {
  const hasAccrualHistory = accrualHistory && accrualHistory.length;
  const hasCostHistory = costHistory && costHistory.length;

  if (!hasAccrualHistory && hasCostHistory) {
    return costHistory;
  } else if (!hasCostHistory && hasCostHistory) {
    return accrualHistory;
  } else if (!hasAccrualHistory && !hasCostHistory) {
    return [];
  }

  let result = [];
  const merged = unionBy(costHistory, accrualHistory, 'year');
  let startYear = maxBy(merged, 'year').year;
  const endYear = minBy(merged, 'year').year;

  while (startYear >= endYear) {
    const costHistoryValue = costHistory.find((el) => el.year === startYear) || {};
    const accrualHistoryValue = filter(accrualHistory, { accrualYear: startYear });

    if (!isEmpty(accrualHistoryValue)) {
      forEach(accrualHistoryValue, (accrual) =>
        result.push(Object.assign({}, costHistoryValue, accrual)),
      );
    } else if (!isEmpty(costHistoryValue)) {
      result.push(Object.assign({}, costHistoryValue, accrualHistoryValue));
    }

    startYear -= 1;
  }

  return result;
}

function mapLand(data) {
  return {
    ...mapCommonProperties(data),
    ...mapCommonRealEstateProperties(data),
    costHistory: mapCostHistory(data.costHistory),
    codeSkz31: data.codeSkz31,
  };
}

function mapHouse(data) {
  return {
    ...mapCommonProperties(data),
    ...mapCommonRealEstateProperties(data),

    conventionalNumber: data.conventionalNumber,
    inventoryNumber: data.inventoryNumber,
    costHistory: mapCostHistory(data.costHistory),
  };
}

function mapCar(data) {
  return {
    ...mapCommonProperties(data),
    ...mapCommonTransportProperties(data),

    model: data.autoModel,
    engineVolume: data.engineVolume,
  };
}

function mapBoat(data) {
  return {
    ...mapCommonProperties(data),
    ...mapCommonTransportProperties(data),

    basePlace: data.basePlace,
    vesselName: data.vesselName,
    engineCount: data.enginesCount,
    vesselClass: data.vesselClass,
    grossCapacity: data.grossCapacity,
  };
}

function mapPlane(data) {
  return {
    ...mapCommonProperties(data),
    ...mapCommonTransportProperties(data),

    basePlace: data.basePlace,
    vesselName: data.vesselName,
    engineType: data.enginesType,
    engineCount: data.enginesCount,
    serialNumber: data.serialNumber,
  };
}

function mapCommonProperties(data) {
  return {
    id: data.fidRight,
    fidObject: data.fidObject,
    fidRight: data.fidRight,
    title: data.pseudonym,
    exemptions: data.objectTaxOffs,
    personExemption: data.personExemption,
    objectExemption: data.objectTaxOffs,
    taxAuthority: {
      code: data.taxAuthority.taxCode,
      title: data.taxAuthority.name,
      shortTitle: data.taxAuthority.shortName,
    },
    propertyType: data.propertyType,
    propertyRightType: data.propertyRight.skpvzuName,
    skvpzuCode: data.propertyRight.skvpzuCode,
    shareNumerator: data.propertyRight.shareNumerator,
    shareDenominator: data.propertyRight.shareDenominator,
    registrationDate: data.propertyRight.startDate,
    registrationEndDate: data.propertyRight.endDate,
    registrationIsStopped: data.propertyRight.isStopped,
    accrualHistory: mapAccrualHistory(data.accrualHistory, data.fidRight),
    formula: data.formula,
    sntsCode: data.sntsCode,
    sntsType: data.sntsType,
    changeToAccept: data.changeToAccept,
  };
}

function mapAccrualHistory(accrualHistory = [], fidRight) {
  if (!accrualHistory || !accrualHistory.length) {
    return [];
  }

  let accruals = accrualHistory.map((accrualData) => {
    return Object.assign({}, accrualData, {
      formula: (accrualData.formula || []).map((formula) =>
        Object.assign({}, formula, {
          id: `${fidRight}.${accrualData.year}.${formula.iriflFormId}`,
        }),
      ),
      accrualYear: +accrualData.year,
      personExemption: accrualData.exemption,
      accrualSum: accrualData.value,
    });
  });

  return accruals.sort((a1, a2) => {
    const yearDiff = a2.accrualYear - a1.accrualYear;
    const iriflDiff = a2.fidIrifl - a1.fidIrifl;
    if (yearDiff > 0) {
      return 1;
    } else if (yearDiff < 0) {
      return -1;
    } else {
      return iriflDiff;
    }
  });
}

function mapCommonRealEstateProperties(data) {
  return {
    address: data.addressKladr,
    area: data.area,
    areaMeasure: data.measureName,
    cadastralNumber: data.cadastralNumber,
    cadastreDate: data.cadastreDate,
  };
}

function mapCommonTransportProperties(data) {
  return {
    vin: data.vin,
    useTerm: data.useTerm,
    isStolen: data.stolen,
    sntsType: data.sntsType,
    sntsCode: data.sntsCode,
    category: data.category,
    horsepower: data.horsePower,
    returnDate: data.returnDate,
    manufactureYear: data.manufactureYear,
    searchStartDate: data.searchStartDate,
    registrationSign: data.registrationSign,
  };
}

function mapCostHistory(costHistory) {
  if (!costHistory || !costHistory.length) {
    return [];
  }

  return costHistory
    .sort((el1, el2) => new Date(el2.valuationDate) - new Date(el1.valuationDate))
    .map((el) =>
      Object.assign({}, el, {
        valuationDate: moment.fromServerDatetime(el.valuationDate),
        costSum: el.value,
      }),
    );
}

export function createPropertyObject(property) {
  let object = {};
  switch (property.propertyType) {
    case propertyObjectTypes.land:
      object = mapLand(property);
      break;
    case propertyObjectTypes.house:
      object = mapHouse(property);
      break;
    case propertyObjectTypes.car:
      object = mapCar(property);
      break;
    case propertyObjectTypes.boat:
      object = mapBoat(property);
      break;
    case propertyObjectTypes.plane:
      object = mapPlane(property);
      break;
    default:
      object = null;
  }

  const isRealEstate =
    property.propertyType === propertyObjectTypes.land ||
    property.propertyType === propertyObjectTypes.house;

  if (object) {
    if (isRealEstate) {
      object.accrualHistory = extendAccrualHistory(object.accrualHistory, object.costHistory);
    }
    Object.defineProperty(object, 'originalProperties', {
      get() {
        return property;
      },
    });
  }

  return object;
}
